import { useEffect, useRef, useState } from 'react';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { api } from '../../api';
import { qrPalette } from '../../theme/tokens';
import { localiseDigits } from '@bookingdei/i18n/format-number';
import { useLanguage } from '../../shell/language-context';
import { useSettings } from '../../shell/settings-context';
import { useResource } from '../../shell/use-resource';
import { HomeIcon } from './home-icons';
import type { HomeIconName } from './home-icons';
import { branchLabel, displayUrl, shareUrl } from './home-model';
import { SHARE_CHANNELS, shareIntent } from './share-targets';
import type { ShareChannel } from './share-targets';
import type { Business, CatalogItem } from '../../mock/seeds/schema-types';

const CHANNEL_KEY: Record<ShareChannel, MessageKey> = {
  facebook: 'dc71.share.facebook',
  whatsapp: 'dc71.share.whatsapp',
  instagram: 'dc71.share.instagram',
  telegram: 'dc71.share.telegram',
  sms: 'dc71.share.sms',
  email: 'dc71.share.email',
};
const CHANNEL_ICON: Record<ShareChannel, HomeIconName> = {
  facebook: 'facebook',
  whatsapp: 'whatsapp',
  instagram: 'instagram',
  telegram: 'telegram',
  sms: 'sms',
  email: 'mail',
};

function svgDataUri(svg: string): string {
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

// Rasterise the server QR SVG on white paper and download it as PNG (SVG fallback).
// Duplicated from the QR studio on purpose (CB-D3). Never runs under jsdom.
function downloadQrPng(svg: string | undefined, filename: string): void {
  if (!svg || typeof document === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) return;
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
  const save = (href: string, name: string) => {
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    a.click();
  };
  const img = new Image();
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 640;
      canvas.height = 640;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('no 2d context');
      ctx.fillStyle = qrPalette.paper;
      ctx.fillRect(0, 0, 640, 640);
      ctx.drawImage(img, 0, 0, 640, 640);
      save(canvas.toDataURL('image/png'), filename);
    } catch {
      save(url, filename.replace(/\.png$/, '.svg'));
    } finally {
      URL.revokeObjectURL(url);
    }
  };
  img.onerror = () => {
    save(url, filename.replace(/\.png$/, '.svg'));
    URL.revokeObjectURL(url);
  };
  img.src = url;
}

// "Your booking link" (M:767-845): ① which link — a business tab (more than one
// business) and a service select (whole business or one service, `?l=`), ② copy and
// send — the public link (web_base_url + /b/{slug}, API-49) with Copy and six share
// channels, ③ the QR for the same scope from GET /merchant/businesses/{id}/qr
// (FR-B13a) with Download PNG.
export function HomeShareCard({ businesses }: { businesses: readonly Business[] }) {
  const { t, lang } = useLanguage();
  const { settings } = useSettings();
  const [bizId, setBizId] = useState<string>(businesses[0]?.id ?? '');
  const [itemId, setItemId] = useState<string>('all');
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (!businesses.some((b) => b.id === bizId)) setBizId(businesses[0]?.id ?? '');
  }, [businesses, bizId]);

  const biz = businesses.find((b) => b.id === bizId);
  const catalog = useResource<CatalogItem[]>(
    async (signal) =>
      bizId ? ((await api.GET('/merchant/businesses/{id}/catalog', { params: { path: { id: bizId } }, signal })).items ?? []) : [],
    [bizId],
  );
  const items = (catalog.data ?? []).filter((c) => c.status === 'active' || (c.status === undefined && c.active !== false));
  const item = items.find((c) => c.id === itemId);
  const itemName = (c: CatalogItem) => (lang === 'bn' && c.name_bn ? c.name_bn : c.name_en);

  const qr = useResource<string | undefined>(
    async (signal) =>
      bizId
        ? ((await api.GET('/merchant/businesses/{id}/qr', {
            signal,
            params: { path: { id: bizId }, query: item?.slug ? { item: item.slug } : {} },
          })) as unknown as string)
        : undefined,
    [bizId, item?.slug],
  );

  const link = biz ? shareUrl(settings?.web_base_url ?? '', biz.slug, item?.slug) : '';
  const bizLabel = (b: Business) => branchLabel(lang === 'bn' && b.name_bn ? b.name_bn : b.name);

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(link);
    } catch {
      /* clipboard blocked — the link stays visible to copy by hand */
    }
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1600);
  };
  const share = (c: ShareChannel) => {
    const href = shareIntent(c, link);
    if (!href) {
      void copy();
      return;
    }
    if (typeof window !== 'undefined') window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="hm-card" aria-labelledby="hm-share-title">
      <div className="hm-card__head">
        <h3 id="hm-share-title" className="hm-card__title">
          {t('dc71.share.title')}
        </h3>
      </div>
      <div className="hm-share">
        <div className="hm-share__main">
          <p className="hm-share__hint">{t('dc71.share.hint')}</p>
          <div className="hm-share__step">
            <span className="hm-step">
              <span className="hm-step__n">{localiseDigits(lang, 1)}</span>
              {t('dc71.share.scope')}
            </span>
            {businesses.length > 1 && (
              <div className="hm-share__tabs" role="tablist" aria-label={t('dc71.share.scope')}>
                {businesses.map((b) => {
                  const on = b.id === bizId;
                  return (
                    <button
                      key={b.id}
                      type="button"
                      role="tab"
                      aria-selected={on}
                      data-share-biz={b.id}
                      className={on ? 'hm-share__tab is-on' : 'hm-share__tab'}
                      onClick={() => {
                        setBizId(b.id);
                        setItemId('all');
                      }}
                    >
                      <span className="hm-share__tabdot" />
                      {bizLabel(b)}
                    </button>
                  );
                })}
              </div>
            )}
            <select className="hm-select" aria-label={t('dc71.share.service')} value={itemId} onChange={(e) => setItemId(e.target.value)}>
              <option value="all">{t('dc71.share.whole')}</option>
              {items.map((c) => (
                <option key={c.id} value={c.id}>
                  {formatMessage(lang, 'dc71.share.only', { name: itemName(c) })}
                </option>
              ))}
            </select>
          </div>
          <span className="hm-step hm-step--gap">
            <span className="hm-step__n">{localiseDigits(lang, 2)}</span>
            {t('dc71.share.send')}
          </span>
          <div className="hm-share__link">
            <HomeIcon name="link" size={16} />
            <span className="hm-share__url" title={link}>
              {displayUrl(link)}
            </span>
            <button type="button" className="hm-share__copy" onClick={copy}>
              <HomeIcon name="copy" size={15} />
              <span>{copied ? t('dc71.share.copied') : t('dc71.share.copy')}</span>
            </button>
          </div>
          <div className="hm-share__channels">
            {SHARE_CHANNELS.map((c, i) => (
              <button key={c} type="button" className={`hm-share__ch hm-share__ch--${i + 1}`} onClick={() => share(c)}>
                <HomeIcon name={CHANNEL_ICON[c]} size={16} />
                <span>{t(CHANNEL_KEY[c])}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="hm-qr">
          <span className="hm-step">
            <span className="hm-step__n">{localiseDigits(lang, 3)}</span>
            {t('dc71.share.qr_title')}
          </span>
          <span className="hm-qr__img" role="img" aria-label={t('dc71.share.qr_title')}>
            {qr.data && <img src={svgDataUri(qr.data)} alt="" width={136} height={136} />}
          </span>
          <span className="hm-qr__scope">
            {businesses.length > 1 && biz ? `${bizLabel(biz)} · ` : ''}
            {item ? itemName(item) : t('dc71.share.qr_all')}
          </span>
          <span className="hm-qr__help">{t('dc71.share.qr_help')}</span>
          <button type="button" className="hm-qr__dl" onClick={() => downloadQrPng(qr.data, `${biz?.slug ?? 'qr'}${item?.slug ? `-${item.slug}` : ''}.png`)}>
            <HomeIcon name="download" size={15} />
            <span>{t('dc71.share.qr_download')}</span>
          </button>
        </div>
      </div>
    </section>
  );
}
